/**
 * Country → University cascade for every course/university filter surface.
 *
 * A university's country is a CUSTOM FIELD value (the study-abroad "Country"
 * field on the `university` context), surfaced on list payloads as
 * `university.customInputs.country` — a string, or an array when the field is
 * multi-valued. Nothing on the University table itself says "UK".
 *
 * Every picker that offers Country AND University side by side must narrow
 * the university list to the picked country: otherwise a counsellor who has
 * chosen "UK" is handed Lithuanian, French and US universities in the very
 * next dropdown (the 2026-09-07 report from the lead drawer's Course tab).
 * Matching is case-insensitive and whitespace-trimmed, the same way the
 * server's `country=<value>` university filter compares.
 */

export const ALL_COUNTRIES = "all";

/** A university row as list payloads ship it (only the fields read here). */
export type UniversityRow = {
  id?: unknown;
  name?: unknown;
  customInputs?: Record<string, unknown> | null;
};
type UniversityLike = UniversityRow | null | undefined;

const normalize = (v: unknown): string => String(v ?? "").trim().toLowerCase();

/** Flat, de-duplicated list of a university's country value(s). */
export function getUniversityCountries(university: UniversityLike): string[] {
  const inputs = university?.customInputs;
  if (!inputs || typeof inputs !== "object") return [];
  const raw = inputs.country ?? inputs.Country;
  if (raw == null || raw === "") return [];
  const values = Array.isArray(raw) ? raw : [raw];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const v of values) {
    const s = String(v ?? "").trim();
    if (!s || seen.has(s.toLowerCase())) continue;
    seen.add(s.toLowerCase());
    out.push(s);
  }
  return out;
}

/** True when the university sits in `country` ("all"/empty → every university). */
export function universityInCountry(
  university: UniversityLike,
  country: string | null | undefined,
): boolean {
  const wanted = normalize(country);
  if (!wanted || wanted === ALL_COUNTRIES) return true;
  return getUniversityCountries(university).some((c) => normalize(c) === wanted);
}

/** The subset of `universities` in `country` ("all"/empty → the full list). */
export function universitiesInCountry<T extends UniversityRow>(
  universities: T[] | null | undefined,
  country: string | null | undefined,
): T[] {
  const list = universities ?? [];
  const wanted = normalize(country);
  if (!wanted || wanted === ALL_COUNTRIES) return list;
  return list.filter((u) => universityInCountry(u, country));
}

/**
 * The university value a picker should hold after the country changes: the
 * current pick if it is still inside the new country, else `fallback`
 * (typically "all" / ""). Keeps a stale selection from silently filtering
 * the course list to a university the dropdown no longer even shows.
 */
export function reconcileUniversityForCountry<T extends UniversityRow>(
  universities: T[] | null | undefined,
  country: string | null | undefined,
  selectedUniversityId: string | null | undefined,
  fallback: string,
): string {
  const sel = String(selectedUniversityId ?? "").trim();
  if (!sel || sel === fallback) return fallback;
  const stillValid = universitiesInCountry(universities, country).some(
    (u) => String(u?.id) === sel,
  );
  return stillValid ? sel : fallback;
}
